<template>
  <div
    class="form-group"
    :class="{
      'form-group--has-error': localError,
      'form-group--has-success': !localError && touched,
    }"
  >
    <label :for="id">
      {{ label }}
      <span v-if="info" class="form-group__info" v-tippy="{ content: info }">
        <BaseIcon name="info" size="14" />
      </span>
    </label>

    <div class="form-group__tag-container">
      <div v-for="(tag, index) in tags" :key="index" class="form-group__tag">
        {{ tag }}
        <button
          type="button"
          class="form-group__tag-remove"
          @click="removeTag(index)"
        >
          <BaseIcon name="xmark" size="12" />
        </button>
      </div>
      <input
        :type="type"
        :id="id"
        :placeholder="placeholder"
        v-model="inputValue"
        @keydown="handleKeydown"
        @keydown.backspace="handleBackspace"
        @blur="handleBlur"
        :disabled="disabled"
      />
    </div>
    <span v-if="localError" class="form-group__error">{{ localError }}</span>
  </div>
</template>

<script>
  import { ref, watch } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default {
    components: {
      BaseIcon,
    },
    props: {
      id: {
        type: String,
        required: true,
      },
      label: {
        type: String,
        required: true,
      },
      modelValue: {
        type: String,
        required: true,
      },
      error: {
        type: String,
        default: '',
      },
      rules: {
        type: Array,
        default: () => [],
      },
      type: {
        type: String,
        default: 'text',
      },
      placeholder: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      info: {
        type: String,
        default: null,
      },
    },
    emits: ['update:modelValue', 'validate'],
    setup(props, { emit }) {
      const localError = ref(props.error);
      const touched = ref(false);
      const inputValue = ref('');
      const tags = ref([]);

      const parseTags = (value) =>
        String(value || '')
          .split(/[,;]/)
          .map((tag) => tag.trim())
          .filter(Boolean);

      // Initialize tags from modelValue
      watch(
        () => props.modelValue,
        (newValue) => {
          tags.value = parseTags(newValue);
        },
        { immediate: true }
      );

      const updateModelValue = () => {
        emit('update:modelValue', tags.value.join(', '));
        emit('validate');
      };

      const addTag = () => {
        const parts = parseTags(inputValue.value);
        if (!parts.length) return;

        const existing = new Set(tags.value.map((tag) => tag.toLowerCase()));
        parts.forEach((part) => {
          if (!existing.has(part.toLowerCase())) {
            tags.value.push(part);
            existing.add(part.toLowerCase());
          }
        });
        inputValue.value = '';
        updateModelValue();
      };

      const handleKeydown = (e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
          e.preventDefault();
          addTag();
        }
      };

      const removeTag = (index) => {
        tags.value.splice(index, 1);
        updateModelValue();
      };

      const handleBackspace = (e) => {
        if (!inputValue.value && tags.value.length) {
          removeTag(tags.value.length - 1);
        }
      };

      const handleBlur = () => {
        touched.value = true;
        addTag();
      };

      watch(
        () => props.error,
        (newError) => {
          localError.value = newError;
        },
        { immediate: true }
      );

      return {
        inputValue,
        tags,
        localError,
        touched,
        addTag,
        removeTag,
        handleKeydown,
        handleBackspace,
        handleBlur,
      };
    },
  };
</script>

<style scoped lang="scss">
  .form-group {
    input#notificationRecipients {
      border: none;
      box-shadow: none;
    }

    &__tag-container {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      padding: 0.5rem;
      border: 1px solid var(--slate-200);
      border-radius: 6px;
      min-height: 42px;
      align-items: center;

      input {
        border: none;
        outline: none;
        padding: 0.25rem;
        flex: 1;
        min-width: 120px;
        font-size: 0.875rem;

        &:focus {
          outline: none;
        }
      }
    }

    &__tag {
      background: var(--sky-50);
      color: var(--sky-600);
      border: 1px solid var(--sky-100);
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      display: flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.875rem;
    }

    &__tag-remove {
      background: none;
      border: none;
      color: var(--blue-600);
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        color: var(--blue-700);
      }
    }

    &--has-error {
      .form-group__tag-container {
        border-color: var(--red-500);
      }
    }

    &--has-success {
      .form-group__tag-container {
        border-color: var(--green-500);
      }
    }
  }
</style>
